/**
 * Redux Slice pour la gestion des profils utilisateurs et contacts
 * 
 * Gère l'état du profil utilisateur connecté, ses contacts et les demandes de contact
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { userProfileService, InvitationError, INVITATION_REQUISE, type ResultatSuppressionCompte } from '../../services/userProfileAPI';
import { 
    UserProfile, 
    ContactReference, 
    ContactRequest, 
    UserSearchResult,
    UpdateUserProfileRequest 
} from '../../types/userProfile';
import { LOADING_STATES, NOTIFICATION_SEVERITY } from '../../constants';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { setStatusBar } from '../modals/modalSlice';

/**
 * État du slice userProfile
 */
interface UserProfileState {
    /** Profil de l'utilisateur connecté */
    currentProfile: UserProfile | null;
    /** Liste des contacts */
    contacts: ContactReference[];
    /** Demandes de contact reçues */
    receivedRequests: ContactRequest[];
    /** Demandes de contact envoyées */
    sentRequests: ContactRequest[];
    /** Résultats de recherche d'utilisateurs */
    searchResults: UserSearchResult[];
    /** Total des résultats de recherche */
    searchTotal: number;
    /** Statut de chargement */
    status: string;
    /** Statut de la recherche */
    searchStatus: string;
    /** Message d'erreur */
    error: string | null;
    /**
     * Porte d'invitation (13/10/2026) : `GET /api/users/me` a répondu 403
     * `INVITATION_REQUISE` — l'identité est connectée mais n'a pas de profil et
     * l'inscription est sur invitation. `ShellSwitch` montre alors la page
     * d'invitation à la place du shell ; `accepterInvitation` la referme.
     */
    invitationRequise: boolean;
}

const initialState: UserProfileState = {
    currentProfile: null,
    contacts: [],
    receivedRequests: [],
    sentRequests: [],
    searchResults: [],
    searchTotal: 0,
    status: LOADING_STATES.IDLE,
    searchStatus: LOADING_STATES.IDLE,
    error: null,
    invitationRequise: false
};

/** Motif de rejet de `fetchMyProfile` : le message, et le code du serveur quand il en a un. */
export interface RejetProfil {
    message: string;
    code?: string;
}

/**
 * Récupère le profil de l'utilisateur connecté
 */
export const fetchMyProfile = createAsyncThunk<UserProfile, any, { rejectValue: RejetProfil }>(
    'userProfile/fetchMyProfile',
    async (instance, { dispatch, rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.getMyProfile(accessToken);
        } catch (error: any) {
            // Porte d'invitation : pas une panne à signaler, une page à montrer
            if (error instanceof InvitationError && error.code === INVITATION_REQUISE) {
                return rejectWithValue({ message: error.message, code: INVITATION_REQUISE });
            }
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors du chargement du profil',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue({ message: error.message || 'Erreur lors du chargement du profil' });
        }
    }
);

/**
 * Crée l'espace sur un code d'invitation (13/10/2026). Réussi, le profil rendu
 * est celui que `GET /api/users/me` aurait créé : la porte se referme et le
 * shell se monte comme pour tout nouveau compte (« Votre espace se prépare »).
 * Refusé, le message du serveur est rendu tel quel à la page (rejet typé).
 */
export const accepterInvitation = createAsyncThunk<UserProfile, { code: string; instance: any }, { rejectValue: string }>(
    'userProfile/accepterInvitation',
    async ({ code, instance }, { rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.accepterInvitation(code, accessToken);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Impossible de vérifier le code d\'invitation.');
        }
    }
);

/**
 * Accepte les textes juridiques courants (C27 SP2). Réussi, le profil rendu porte
 * `consentements` : `ShellSwitch` démonte la porte. Refusé, le message du serveur
 * est rendu tel quel à la page (rejet typé).
 */
export const accepterConsentements = createAsyncThunk<UserProfile, { instance: any }, { rejectValue: string }>(
    'userProfile/accepterConsentements',
    async ({ instance }, { rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.accepterConsentements(accessToken);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Impossible d\'enregistrer votre accord pour l\'instant.');
        }
    }
);

/**
 * Supprime le compte (C27 SP2) : l'adresse retapée confirme. Le store n'est pas
 * vidé ici — la fenêtre dit le résultat puis déconnecte, ce qui recharge tout.
 */
export const supprimerMonCompte = createAsyncThunk<ResultatSuppressionCompte, { email: string; instance: any }, { rejectValue: string }>(
    'userProfile/supprimerMonCompte',
    async ({ email, instance }, { rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.supprimerCompte(email, accessToken);
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Impossible de supprimer le compte pour l\'instant.');
        }
    }
);

/**
 * Met à jour le profil de l'utilisateur connecté
 */
export const updateMyProfile = createAsyncThunk(
    'userProfile/updateMyProfile',
    async (
        { data, instance }: { data: UpdateUserProfileRequest; instance: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            const result = await userProfileService.updateMyProfile(data, accessToken);
            
            dispatch(setStatusBar({
                open: true,
                message: 'Profil mis à jour avec succès',
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return result;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors de la mise à jour du profil',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Récupère les contacts de l'utilisateur
 */
export const fetchContacts = createAsyncThunk(
    'userProfile/fetchContacts',
    async (instance: any, { rejectWithValue }) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.getContacts(accessToken, 'accepted');
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Recherche des utilisateurs
 */
export const searchUsers = createAsyncThunk(
    'userProfile/searchUsers',
    async (
        { query, instance, options }: { 
            query: string; 
            instance: any; 
            options?: { page?: number; limit?: number } 
        },
        { rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return await userProfileService.searchUsers(query, accessToken, options);
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Envoie une demande de contact
 */
export const sendContactRequest = createAsyncThunk(
    'userProfile/sendContactRequest',
    async (
        { toUserId, message, instance }: { toUserId: string; message?: string; instance: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            const result = await userProfileService.sendContactRequest(toUserId, message, accessToken);
            
            dispatch(setStatusBar({
                open: true,
                message: 'Demande de contact envoyée',
                severity: NOTIFICATION_SEVERITY.SUCCESS
            }));
            
            return result;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors de l\'envoi de la demande',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Récupère les demandes de contact
 */
export const fetchContactRequests = createAsyncThunk(
    'userProfile/fetchContactRequests',
    async (
        { instance, type }: { instance: any; type: 'received' | 'sent' | 'all' },
        { rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            return {
                type,
                requests: await userProfileService.getContactRequests(accessToken, type)
            };
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Répond à une demande de contact
 */
export const respondToContactRequest = createAsyncThunk(
    'userProfile/respondToContactRequest',
    async (
        { requestId, accept, instance }: { requestId: string; accept: boolean; instance: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            await userProfileService.respondToContactRequest(requestId, accept, accessToken);
            
            dispatch(setStatusBar({
                open: true,
                message: accept ? 'Contact ajouté !' : 'Demande refusée',
                severity: accept ? NOTIFICATION_SEVERITY.SUCCESS : NOTIFICATION_SEVERITY.INFO
            }));
            
            // Recharger les contacts et demandes
            dispatch(fetchContacts(instance));
            
            return { requestId, accept };
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors du traitement de la demande',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Supprime un contact
 */
export const removeContact = createAsyncThunk(
    'userProfile/removeContact',
    async (
        { contactUserId, instance }: { contactUserId: string; instance: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const accessToken = await getTokenRedirect(tokenRequest, instance);
            if (!accessToken) {
                throw new Error('Impossible d\'obtenir le token d\'accès');
            }
            await userProfileService.removeContact(contactUserId, accessToken);
            
            dispatch(setStatusBar({
                open: true,
                message: 'Contact supprimé',
                severity: NOTIFICATION_SEVERITY.INFO
            }));
            
            return contactUserId;
        } catch (error: any) {
            dispatch(setStatusBar({
                open: true,
                message: error.message || 'Erreur lors de la suppression du contact',
                severity: NOTIFICATION_SEVERITY.ERROR
            }));
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Slice Redux pour les profils utilisateurs
 */
const userProfileSlice = createSlice({
    name: 'userProfile',
    initialState,
    reducers: {
        /** Réinitialise l'état */
        resetUserProfile: () => initialState,
        /** Efface les résultats de recherche */
        clearSearchResults: (state) => {
            state.searchResults = [];
            state.searchTotal = 0;
            state.searchStatus = LOADING_STATES.IDLE;
        },
        /** Efface les erreurs */
        clearError: (state) => {
            state.error = null;
        },
        /**
         * Le profil rendu par un code accepté (C26 SP2) : la porte d'invitation le pose
         * elle-même, après avoir montré « Vous avez rejoint … » quand le code est celui
         * d'une organisation — même effet que `accepterInvitation.fulfilled`.
         */
        invitationAcceptee: (state, action: PayloadAction<UserProfile>) => {
            state.status = LOADING_STATES.SUCCEEDED;
            state.error = null;
            state.currentProfile = action.payload;
            state.contacts = action.payload.contacts?.filter(c => c.status === 'accepted') || [];
            state.invitationRequise = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchMyProfile
            .addCase(fetchMyProfile.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchMyProfile.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.currentProfile = action.payload;
                state.contacts = action.payload.contacts?.filter(c => c.status === 'accepted') || [];
                state.invitationRequise = false;
            })
            .addCase(fetchMyProfile.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload?.message ?? action.error.message ?? null;
                state.invitationRequise = action.payload?.code === INVITATION_REQUISE;
            })

            // accepterInvitation (13/10/2026) : le profil créé entre comme après fetchMyProfile
            .addCase(accepterInvitation.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.error = null;
                state.currentProfile = action.payload;
                state.contacts = action.payload.contacts?.filter(c => c.status === 'accepted') || [];
                state.invitationRequise = false;
            })
            
            // accepterConsentements (C27 SP2) : le profil rendu porte le consentement
            .addCase(accepterConsentements.fulfilled, (state, action) => {
                state.currentProfile = action.payload;
            })

            // updateMyProfile
            .addCase(updateMyProfile.fulfilled, (state, action) => {
                state.currentProfile = action.payload;
            })
            
            // fetchContacts
            .addCase(fetchContacts.fulfilled, (state, action) => {
                state.contacts = action.payload;
            })
            
            // searchUsers
            .addCase(searchUsers.pending, (state) => {
                state.searchStatus = LOADING_STATES.LOADING;
            })
            .addCase(searchUsers.fulfilled, (state, action) => {
                state.searchStatus = LOADING_STATES.SUCCEEDED;
                state.searchResults = action.payload.data;
                state.searchTotal = action.payload.total;
            })
            .addCase(searchUsers.rejected, (state) => {
                state.searchStatus = LOADING_STATES.FAILED;
            })
            
            // sendContactRequest
            .addCase(sendContactRequest.fulfilled, (state, action) => {
                state.sentRequests.push(action.payload);
                // Mettre à jour le résultat de recherche
                const idx = state.searchResults.findIndex(u => u.userId === action.payload.toUserId);
                if (idx !== -1) {
                    state.searchResults[idx].hasPendingRequest = true;
                }
            })
            
            // fetchContactRequests
            .addCase(fetchContactRequests.fulfilled, (state, action) => {
                if (action.payload.type === 'received') {
                    state.receivedRequests = action.payload.requests;
                } else if (action.payload.type === 'sent') {
                    state.sentRequests = action.payload.requests;
                } else {
                    // type 'all' - séparer les demandes
                    const currentUserId = state.currentProfile?.userId;
                    state.receivedRequests = action.payload.requests.filter(
                        r => r.toUserId === currentUserId
                    );
                    state.sentRequests = action.payload.requests.filter(
                        r => r.fromUserId === currentUserId
                    );
                }
            })
            
            // respondToContactRequest
            .addCase(respondToContactRequest.fulfilled, (state, action) => {
                // Retirer la demande traitée
                state.receivedRequests = state.receivedRequests.filter(
                    r => r._id !== action.payload.requestId
                );
            })
            
            // removeContact
            .addCase(removeContact.fulfilled, (state, action) => {
                state.contacts = state.contacts.filter(c => c.userId !== action.payload);
            });
    }
});

// Actions
export const { resetUserProfile, clearSearchResults, clearError, invitationAcceptee } = userProfileSlice.actions;

// Sélecteurs
export const getCurrentProfile = (state: RootState) => state.userProfile.currentProfile;
export const getContacts = (state: RootState) => state.userProfile.contacts;
export const getReceivedRequests = (state: RootState) => state.userProfile.receivedRequests;
export const getSentRequests = (state: RootState) => state.userProfile.sentRequests;
export const getSearchResults = (state: RootState) => state.userProfile.searchResults;
export const getSearchTotal = (state: RootState) => state.userProfile.searchTotal;
export const getUserProfileStatus = (state: RootState) => state.userProfile.status;
export const getSearchStatus = (state: RootState) => state.userProfile.searchStatus;
export const getUserProfileError = (state: RootState) => state.userProfile.error;
export const getInvitationRequise = (state: RootState) => state.userProfile.invitationRequise;

export default userProfileSlice.reducer;
